import { BehaviorSubject, Subject } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { isNil, orderBy } from 'lodash';
import * as L from 'leaflet';
import { MapStoreService } from './map-store.service';
import { MathUtilityService } from '@app/shared/services/math-utility.service';
import { LampOptions } from '../interfaces/LampOptions';
import { filter, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MapService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  private zoomLevelStore: BehaviorSubject<any | null> = new BehaviorSubject(null);
  public manualSelectModeChange: Subject<boolean> = new Subject<boolean>();
  public isClickedModeChange: Subject<boolean> = new Subject<boolean>();
  public zoomLevel$ = this.zoomLevelStore.asObservable();

  constructor(public mapStoreService: MapStoreService, public mathUtilityService: MathUtilityService) {
    this.mapStoreService.map$
      .pipe(
        filter((res: L.Map | null): res is L.Map => !isNil(res)),
        tap((lMap: L.Map) => lMap.on('zoomend', () => this.zoomLevelStore.next(lMap.getZoom()))),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public calculateForMedianMarker(markers: any) {
    let _lat: any[] = [];
    let _lng: any[] = [];

    for (const element of markers) {
      _lat.push(element._latlng.lat);
      _lng.push(element._latlng.lng);
    }

    _lat = orderBy(_lat);
    _lng = orderBy(_lng);

    const medLat = this.mathUtilityService.median(_lat);
    const medLng = this.mathUtilityService.median(_lng);

    return {
      lat: medLat,
      lng: medLng,
    };
  }

  public setClassLamp(lampMarkers: any, options: LampOptions) {
    const elements: any[] = [].concat(lampMarkers || []);

    this.updateLampMarker(elements, options);

    try {
      this.mapStoreService.streetLightCluster.refreshClusters();
    } catch {}
  }

  public updateLampMarker(targets: any, options: LampOptions) {
    const elements: any[] = [].concat(targets || []);

    elements.forEach((target: any) => {
      target.options = Object.assign(target.options, options);

      let divIcon;

      if (target.options.data.hasMultipleLamps) {
        divIcon = this.getMultipleLampIcon(target.options.data.lamps[0].colorCode, target.options.data.lamps.length, target.options);
      } else {
        divIcon = this.getSingleLampIcon(target.options.data.lamps[0].colorCode, target.options);
      }

      target.setIcon(divIcon);
    });
  }

  public getMultipleLampIcon(colorCode: string, lampCount: number, lampData: LampOptions) {
    const classvalue = this.getLampClass(lampData);
    const innerDiv = `<div style="
    background-color: #0091BB;
    color: white;
    width: 20px;
    height: 20px;
    text-align: center;
    margin-left: 10px;
    margin-top: -15px;
    border-radius: 3px;
    vertical-align: middle">${lampCount}</div>`;
    return L.divIcon({
      html: `<div class="${classvalue}"
      style="${this.getLampStyles(colorCode, lampData)}">
      ${innerDiv}
      </div>`,
      popupAnchor: [-100, -330],
    });
  }

  public getSingleLampIcon(colorCode: string, lampData: LampOptions) {
    const classvalue = this.getLampClass(lampData);

    return L.divIcon({
      html: `<div class="${classvalue}"
      style="${this.getLampStyles(colorCode, lampData)}">
      </div>`,
      popupAnchor: [-100, -330],
    });
  }

  public resetLampsIcon() {
    this.mapStoreService.lampMarkers.forEach((lamp) => {
      this.updateLampMarker(lamp, { isSelected: false, isHighlighted: false });
      lamp.selected = false;
    });

    this.mapStoreService.streetLightCluster.refreshClusters();
  }

  hexToRGB(h: string, a: number) {
    let r = '0',
      g = '0',
      b = '0';

    if (h.length === 4) {
      r = '0x' + h[1] + h[1];
      g = '0x' + h[2] + h[2];
      b = '0x' + h[3] + h[3];
    } else if (h.length === 7) {
      r = '0x' + h[1] + h[2];
      g = '0x' + h[3] + h[4];
      b = '0x' + h[5] + h[6];
    }

    return `rgba(${+r}, ${+g}, ${+b}, ${a})`;
  }

  public getLampShadowStyle(color: string, opacity: number) {
    const rgb = this.hexToRGB(`#${color}`, opacity);
    return `-webkit-box-shadow: 0px 0px 0px 15px ${rgb};
    -moz-box-shadow: 0px 0px 0px 15px ${rgb};
    box-shadow: 0px 0px 0px 15px ${rgb};`;
  }

  public getLampStyles(colorCode: string, { isSelected = false, isHighlighted = false }: LampOptions) {
    return `background-color: #${isHighlighted ? 'FFFFFF' : colorCode};
    ${isSelected ? this.getLampShadowStyle(colorCode, 0.3) : ''}
    ${isHighlighted ? this.getLampShadowStyle(colorCode, 1) : ''}
    `;
  }

  public getLampStylesHighlighted(colorCode: string, isHighlighted: boolean) {
    return isHighlighted ? { 'background-color': this.getLampShadowStyle(colorCode, 0.3) } : { 'background-color': '#000000' }; // eslint-disable-line
  }

  public getLampClass({ isSelected = false, isHighlighted = false, isHeatmap = false }: LampOptions) {
    let className = 'lamp-circle';

    className += isSelected ? ' -selected' : '';
    className += isHighlighted ? ' -highlighted' : '';
    className += isHeatmap ? ' -heatmap' : '';

    return className;
  }

  selectMode(modeOn: boolean) {
    this.manualSelectModeChange.next(modeOn);
  }

  lampClicked(isClicked: boolean) {
    this.isClickedModeChange.next(isClicked);
  }

  public getPinLocationMarker() {
    return L.divIcon({
      className: 'custom-div-icon',
      html: `<div class='marker-pin-location'></div>`,
      iconSize: [30, 42],
    });
  }
}
